import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from '../hooks/useTranslation';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OrderSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const amount = searchParams.get('amount');
  const paymentMethod = searchParams.get('payment_method');
  const deliveryTime = searchParams.get('delivery_time');
  const orderType = searchParams.get('order_type');

  // Generate confirmation message based on delivery time and order type
  const getConfirmationMessage = () => {
    const isPickup = orderType === 'pickup';
    const isScheduled = deliveryTime && deliveryTime !== 'asap';
    
    if (isScheduled) {
      // Scheduled order - show the scheduled date/time
      const scheduledMessages = {
        lv: isPickup 
          ? `Paldies par jūsu pasūtījumu! Jūsu pasūtījums būs gatavs: ${deliveryTime}`
          : `Paldies par jūsu pasūtījumu! Jūsu pasūtījums tiks piegādāts: ${deliveryTime}`,
        en: isPickup
          ? `Thank you for your order! Your order will be ready: ${deliveryTime}`
          : `Thank you for your order! Your order will be delivered: ${deliveryTime}`,
        ru: isPickup
          ? `Спасибо за ваш заказ! Ваш заказ будет готов: ${deliveryTime}`
          : `Спасибо за ваш заказ! Ваш заказ будет доставлен: ${deliveryTime}`
      };
      return scheduledMessages[language] || scheduledMessages.lv;
    } else {
      // ASAP order - show 30-60 min message
      return t('thankYouOrder');
    }
  };

  useEffect(() => {
    // Handle cash orders
    if (paymentMethod === 'cash' && orderId && amount) {
      setStatus('success');
      setOrderDetails({
        order_id: orderId,
        amount: parseFloat(amount),
        payment_method: 'cash',
        status: 'confirmed'
      });
      return;
    }
    
    // Handle Stripe card payments
    if (!sessionId) {
      navigate('/');
      return;
    }

    const checkPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(`${BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrderDetails(response.data);
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else {
          setTimeout(() => checkPaymentStatus(attempts + 1), pollInterval);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, orderId, amount, paymentMethod, navigate]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" />
          <h2 className="text-2xl font-bold mb-2">{t('processingPayment')}</h2>
          <p className="text-gray-400">{t('pleaseWait')}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-white rounded-3xl p-12 text-center shadow-2xl">
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-black mb-4">
            {t('orderConfirmed')}
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {getConfirmationMessage()}
          </p>
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="font-bold text-black mb-3">
              {t('orderDetails')}
            </h3>
            {orderDetails && (
              <div className="space-y-2 text-left">
                <p className="text-gray-600">
                  <span className="font-semibold">{t('amount')}:</span> €{
                    orderDetails.payment_method === 'cash' 
                      ? orderDetails.amount.toFixed(2)
                      : (orderDetails.amount_total / 100).toFixed(2)
                  }
                </p>
                <p className="text-gray-600">
                  <span className="font-semibold">{t('status')}:</span> {t('confirmed')}
                </p>
                <p className="text-gray-600">
                  <span className="font-semibold">{t('paymentInfo')}:</span> {
                    orderDetails.payment_method === 'cash' 
                      ? t('cashOnDelivery')
                      : t('creditCard')
                  }
                </p>
              </div>
            )}
          </div>
          <Button
            onClick={() => navigate('/')}
            className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg"
          >
            {t('backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="max-w-2xl w-full bg-white rounded-3xl p-12 text-center shadow-2xl">
        <div className="w-24 h-24 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl text-white">✕</span>
        </div>
        <h1 className="text-4xl font-bold text-black mb-4">
          {status === 'expired' ? t('paymentExpired') : t('paymentError')}
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          {status === 'expired' ? t('sessionExpired') : t('errorProcessing')}
        </p>
        <Button
          onClick={() => navigate('/')}
          className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-lg"
        >
          {t('backToHome')}
        </Button>
      </div>
    </div>
  );
};

export default OrderSuccess;
